// ============================================
// src/components/payments/PaymentEntry.js
// Version without activity_logs table
// ============================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function PaymentEntry({ residents, currentMonth, currentYear, adminId, onPaymentSaved }) {
  const [formData, setFormData] = useState({
    flat_number: '',
    resident_ids: [],
    amount: 2000,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'gpay',
    transaction_ref: '',
    notes: '',
    late_fee: 0
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedFlatResidents, setSelectedFlatResidents] = useState([]);
  const [flatsWithResidents, setFlatsWithResidents] = useState([]);
  const [unpaidFlats, setUnpaidFlats] = useState([]);

  useEffect(() => {
    processFlatData();
  }, [residents, currentMonth, currentYear]);

  const processFlatData = async () => {
    try {
      if (!residents || residents.length === 0) {
        console.warn('No residents data received in PaymentEntry');
        return;
      }

      // Group residents by flat number
      const flatGroups = {};
      residents.forEach(resident => {
        // Skip superadmin from the list
        if (resident.role === 'superadmin') return;
        
        if (!resident.flat_number) {
          console.warn('Resident without flat_number:', resident);
          return;
        }

        if (!flatGroups[resident.flat_number]) {
          flatGroups[resident.flat_number] = [];
        }
        flatGroups[resident.flat_number].push(resident);
      });

      // Convert to array format
      const flatsArray = Object.keys(flatGroups).map(flatNumber => ({
        flat_number: flatNumber,
        residents: flatGroups[flatNumber],
        display_name: `${flatNumber} - ${flatGroups[flatNumber].map(r => r.name).join(' & ')}`,
        primary_contact: flatGroups[flatNumber][0]
      }));

      // Sort by flat number
      flatsArray.sort((a, b) => {
        return a.flat_number.localeCompare(b.flat_number, undefined, { numeric: true, sensitivity: 'base' });
      });

      setFlatsWithResidents(flatsArray);

      // Get unpaid flats
      const { data: paidData, error } = await supabase
        .from('payments')
        .select('resident_id, flat_number')
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear)
        .eq('status', 'paid');

      if (error) {
        console.error('Error fetching payment data:', error);
      }

      const paidResidentIds = paidData ? paidData.map(p => p.resident_id) : [];
      const paidFlatNumbers = paidData ? paidData.map(p => p.flat_number).filter(Boolean) : [];
      
      // Mark flats as unpaid if not in paid list
      const unpaidFlatsList = flatsArray.filter(flat => {
        const isPaid = paidFlatNumbers.includes(flat.flat_number) || 
                       flat.residents.some(resident => paidResidentIds.includes(resident.id));
        return !isPaid;
      });

      setUnpaidFlats(unpaidFlatsList);

    } catch (error) {
      console.error('Error processing flat data:', error);
    }
  };

  const handleFlatChange = (e) => {
    const flatNumber = e.target.value;
    setFormData(prev => ({ ...prev, flat_number: flatNumber }));

    if (flatNumber) {
      const selectedFlat = flatsWithResidents.find(f => f.flat_number === flatNumber);
      if (selectedFlat) {
        setSelectedFlatResidents(selectedFlat.residents);
        setFormData(prev => ({ 
          ...prev, 
          resident_ids: selectedFlat.residents.map(r => r.id)
        }));
      }
    } else {
      setSelectedFlatResidents([]);
      setFormData(prev => ({ ...prev, resident_ids: [] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.flat_number) {
      alert('Please select a flat');
      return;
    }

    setLoading(true);
    setSuccessMessage('');

    try {
      const receiptNumber = `RCP${currentYear}${String(currentMonth).padStart(2, '0')}${Date.now().toString().slice(-4)}`;
      
      // Record payment for the primary resident of the flat
      const primaryResident = selectedFlatResidents[0];
      
      if (!primaryResident) {
        throw new Error('No primary resident found for the selected flat');
      }

      // Check if payment already exists
      const { data: existingPayment } = await supabase
        .from('payments')
        .select('id')
        .eq('resident_id', primaryResident.id)
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear)
        .single();

      let result;
      const paymentData = {
        amount: parseFloat(formData.amount) + parseFloat(formData.late_fee || 0),
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        transaction_ref: formData.transaction_ref,
        status: 'paid',
        late_fee: parseFloat(formData.late_fee || 0),
        notes: formData.notes,
        collected_by: adminId,
        flat_number: formData.flat_number
      };

      if (existingPayment) {
        // Update existing payment
        result = await supabase
          .from('payments')
          .update({
            ...paymentData,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingPayment.id)
          .select();
      } else {
        // Insert new payment
        result = await supabase
          .from('payments')
          .insert([{
            resident_id: primaryResident.id,
            payment_month: currentMonth,
            payment_year: currentYear,
            receipt_number: receiptNumber,
            ...paymentData,
            created_at: new Date().toISOString()
          }])
          .select();
      }

      if (result.error) {
        console.error('Payment save error:', result.error);
        throw result.error;
      }

      console.log('Payment saved successfully:', result.data);

      // Note: Removed activity_logs insertion since table doesn't exist
      // You can add it back later when you create the activity_logs table

      setSuccessMessage(`Payment recorded successfully for Flat ${formData.flat_number}`);
      
      // Reset form
      setFormData({
        flat_number: '',
        resident_ids: [],
        amount: 2000,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'gpay',
        transaction_ref: '',
        notes: '',
        late_fee: 0
      });
      setSelectedFlatResidents([]);
      
      processFlatData();
      if (onPaymentSaved) onPaymentSaved();

      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error saving payment:', error);
      alert(`Failed to save payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const quickSelectUnpaid = () => {
    if (unpaidFlats.length > 0) {
      const firstUnpaid = unpaidFlats[0];
      setFormData(prev => ({ 
        ...prev, 
        flat_number: firstUnpaid.flat_number,
        resident_ids: firstUnpaid.residents.map(r => r.id)
      }));
      setSelectedFlatResidents(firstUnpaid.residents);
    }
  };

  return (
    <div>
      <h3 style={{ marginBottom: '20px', color: '#333' }}>Record Payment Received</h3>
      
      {successMessage && (
        <div style={{
          background: '#d4edda',
          color: '#155724',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ✅ {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          
          {/* Left Column */}
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                Select Flat *
              </label>
              <select
                name="flat_number"
                value={formData.flat_number}
                onChange={handleFlatChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: 'white'
                }}
              >
                <option value="">-- Select Flat --</option>
                
                {/* Unpaid flats first */}
                {unpaidFlats.length > 0 && (
                  <>
                    <option disabled style={{ fontWeight: 'bold', color: '#666' }}>
                      ──── Unpaid Flats ────
                    </option>
                    {unpaidFlats.map(flat => (
                      <option key={`unpaid-${flat.flat_number}`} value={flat.flat_number}>
                        {flat.display_name} ⚠️ (Pending)
                      </option>
                    ))}
                  </>
                )}
                
                {/* Separator */}
                {unpaidFlats.length > 0 && flatsWithResidents.length > unpaidFlats.length && (
                  <option disabled style={{ fontWeight: 'bold', color: '#666' }}>
                    ──── All Flats ────
                  </option>
                )}
                
                {/* All flats */}
                {flatsWithResidents.map(flat => {
                  const isPaid = !unpaidFlats.find(u => u.flat_number === flat.flat_number);
                  if (isPaid || unpaidFlats.length === 0) {
                    return (
                      <option key={`all-${flat.flat_number}`} value={flat.flat_number}>
                        {flat.display_name} {isPaid ? '✓' : ''}
                      </option>
                    );
                  }
                  return null;
                })}
              </select>
              
              {unpaidFlats.length > 0 && (
                <button
                  type="button"
                  onClick={quickSelectUnpaid}
                  style={{
                    marginTop: '5px',
                    padding: '5px 10px',
                    background: '#ffc107',
                    color: '#000',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Quick Select First Unpaid ({unpaidFlats.length} pending)
                </button>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                Amount (₹) *
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                required
                min="0"
                step="100"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                Late Fee (₹)
              </label>
              <input
                type="number"
                name="late_fee"
                value={formData.late_fee}
                onChange={(e) => setFormData(prev => ({ ...prev, late_fee: e.target.value }))}
                min="0"
                step="50"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                Payment Date *
              </label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                Payment Method *
              </label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: 'white'
                }}
              >
                <option value="gpay">GPay</option>
                <option value="phonepe">PhonePe</option>
                <option value="paytm">Paytm</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">Other UPI</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                Transaction Reference
              </label>
              <input
                type="text"
                name="transaction_ref"
                value={formData.transaction_ref}
                onChange={(e) => setFormData(prev => ({ ...prev, transaction_ref: e.target.value }))}
                placeholder="UTR/Transaction ID"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
            Notes (Optional)
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows="3"
            placeholder="Any additional notes..."
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '16px',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Selected Flat Info */}
        {selectedFlatResidents.length > 0 && (
          <div style={{
            background: '#f8f9fa',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Selected Flat Details:</h4>
            <div style={{ marginBottom: '10px' }}>
              <strong>Flat:</strong> {formData.flat_number}
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong>Residents:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {selectedFlatResidents.map(resident => (
                  <li key={resident.id}>
                    {resident.name} ({resident.role}) - 📱 {resident.phone}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ 
              padding: '10px', 
              background: '#e7f3ff', 
              borderRadius: '5px',
              fontSize: '14px',
              color: '#0066cc'
            }}>
              💡 Payment will be recorded for the entire flat. All residents of this flat will be marked as paid.
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="submit"
            disabled={loading || flatsWithResidents.length === 0}
            style={{
              flex: 1,
              padding: '12px',
              background: loading || flatsWithResidents.length === 0 ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading || flatsWithResidents.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s'
            }}
          >
            {loading ? 'Saving...' : 'Save Payment'}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setFormData({
                flat_number: '',
                resident_ids: [],
                amount: 2000,
                payment_date: new Date().toISOString().split('T')[0],
                payment_method: 'gpay',
                transaction_ref: '',
                notes: '',
                late_fee: 0
              });
              setSelectedFlatResidents([]);
            }}
            style={{
              padding: '12px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          >
            Clear
          </button>
        </div>
      </form>
    </div>
  );
}

export default PaymentEntry;